import React, { useState } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ListPlus, Loader2, Lock, Save, Trash2, Trophy, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { teamListApi } from '@/lib/bracket/shareApi';

const Empty = ({ children }) => <p className="px-2 py-1.5 text-xs text-slate-400">{children}</p>;

const ListItem = ({ l, onFill, onDelete }) => (
  <DropdownMenuItem onSelect={() => onFill(l.teams, l.name)} className="flex items-center gap-2" data-testid={`team-list-item-${l.id}`}>
    <Users className="w-4 h-4 text-slate-400 shrink-0" />
    <span className="flex-1 min-w-0 truncate">{l.name}</span>
    <span className="text-[11px] text-slate-400 tabular-nums">{l.teams.length}</span>
    <button type="button" aria-label={`Delete ${l.name}`} data-testid={`team-list-delete-${l.id}`}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(l); }}
      className="p-1 rounded text-slate-300 hover:text-[#CF142B] hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
  </DropdownMenuItem>
);

// Pro: fill the starting teams from a saved list or a league, or save the current teams as a list.
export const TeamListMenu = ({ isPro, onLocked, onFill, onSave }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = () => Promise.all([teamListApi.list(), teamListApi.leagues()])
    .then(([a, b]) => setData({ lists: a.lists, leagues: b.leagues }))
    .catch((e) => { toast.error('Could not load your teams', { description: e.message }); setOpen(false); });
  const toggle = (o) => {
    if (o && !isPro) { onLocked(); return; }
    setOpen(o);
    if (o) { setData(null); load(); }
  };
  const remove = async (l) => {
    try { await teamListApi.remove(l.id); setData((d) => ({ ...d, lists: d.lists.filter((x) => x.id !== l.id) })); toast.success(`Deleted "${l.name}"`); } catch (e) { toast.error(e.message); }
  };
  const lock = !isPro && <Lock className="w-3 h-3 ml-1 text-slate-400" />;
  return (
    <div className="flex items-center gap-1.5">
      <DropdownMenu open={open} onOpenChange={toggle}>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-7 px-2 text-xs" data-testid="team-list-fill-btn"><ListPlus className="w-3.5 h-3.5 mr-1" />Fill teams{lock}<ChevronDown className="w-3 h-3 ml-0.5" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 max-h-[360px] overflow-y-auto" data-testid="team-list-menu">
          {!data ? <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#00247D]" /></div> : (
            <>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Saved lists</DropdownMenuLabel>
              {data.lists.length ? data.lists.map((l) => <ListItem key={l.id} l={l} onFill={onFill} onDelete={remove} />) : <Empty>No saved lists yet. Type your teams, then Save list.</Empty>}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-400">Your leagues</DropdownMenuLabel>
              {data.leagues.length ? data.leagues.map((g) => (
                <DropdownMenuItem key={g.event_id} onSelect={() => onFill(g.teams, g.name)} className="flex items-center gap-2" data-testid={`team-league-item-${g.event_id}`}>
                  <Trophy className="w-4 h-4 text-amber-500 shrink-0" /><span className="flex-1 min-w-0 truncate">{g.name}</span>
                  <span className="text-[11px] text-slate-400 tabular-nums">{g.teams.length}</span>
                </DropdownMenuItem>
              )) : <Empty>No leagues with teams yet.</Empty>}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => (isPro ? onSave() : onLocked())} data-testid="team-list-save-btn">
        <Save className="w-3.5 h-3.5 mr-1" />Save list{lock}
      </Button>
    </div>
  );
};
